<template>
  <div class="map-chart" :style="{width: config.chartWidth+'px',height:config.chartHeight+'px'}">
    <div class="map-chart-wrapper">
      <img
        v-if="borderImgUrl" class="map-chart-border" :src="borderImgUrl" alt=""
        :style="chartBorderTemplateImageStyle">
      <div class="map-chart-title" :style="titleBarInlineStyle">
        {{ config.chartTitle }}
        <el-tooltip v-if="config.chartViewData.showIndicator" class="item" :open-delay="500" effect="dark" placement="top">
          <div slot="content" v-html="indicatorDescription"></div>
          <i class="el-icon-question"></i>
        </el-tooltip>
      </div>
    </div>
    <div ref="mapChart" class="map-chart-container"></div>
  </div>
</template>

<script>
import _ from 'lodash'
import * as echarts from 'echarts'
import { COLOR_LIST } from '@/utils/defaultColorData'
import { imgURLtoBase64, randomString, hexToRgba } from '@/utils/common'
import { relateBus } from '@/eventBus'
import { mapBaseOption } from './options'
import ChartMixin from './mixin'
import { MAP_ROUTE_LINE_COLOR } from '@/utils/mapRouteGeo'

export default {
  mixins: [ChartMixin],

  data () {
    return {
      mapName: '',
      /** 递增序号，丢弃过期的 geojson fetch 结果（vue/no-reserved-keys 禁止 data 键以 _ 开头） */
      getMapRequestSerial: 0,
      /** 当前 geojson 全部区域名（分段热力时用于无数据地区默认 0） */
      mapFeatureNameList: [],
    }
  },
  computed: {
    chartStyle () {
      return this.config.chartSet.chartStyle
    },
    /** 侧栏「组件文字」颜色：区域标签、tooltip、分段图例文案 */
    mapTextColor () {
      const c = this.chartStyle?.color
      return c != null && c !== '' ? c : '#ccc'
    },

    // 指标库/API 回填为 { x, y_0, label? }[]，无表头行、无 col_*；自定义表格数据为 [表头, 数据行...]
    isIndicatorApiChartData () {
      const cd = this.chartData
      if (!cd?.length || !cd[0]) return false
      const first = cd[0]
      return !('col_0' in first) && (first.x !== undefined || first.y_0 !== undefined)
    },
    columnName () {
      const list = []
      if (!this.chartData?.length) return []
      if (this.isIndicatorApiChartData) {
        // 与 mixin 回填一致：value 为 [ y, label ]（地区名 + 指标说明文案）
        return ['地区', '说明']
      }
      const colKeyList = Object.keys(this.chartData[0])
      colKeyList.forEach(item => {
        if (this.chartData[1][item] !== '' && this.chartData[1][item] !== undefined) {
          list.push(this.chartData[0][item])
        }
      })
      return list.slice(1)
    },
    chartData () {
      return this.config.chartViewData.chartData
    },
    renderData () {
      if (!this.chartData?.length || !Array.isArray(this.chartData)) {
        return []
      }
      const list = []
      const rows = this.isIndicatorApiChartData ? this.chartData : this.chartData.slice(1)
      rows.forEach(item => {
        const value = []
        if (item.x) {
          let curName
          if (this.config.chartSet.chartStyle.map.length === 1) {
            curName = this.mapNameList.find(bar => item.x.indexOf(bar) !== -1)
          }
          list.push({
            name: curName || item.x,
            value: [item.y_0, item.label],
          })
        } else {
          this.columnName.forEach((name, index) => {
            value.push(item[`col_${index + 1}`])
          })
          list.push({
            name: item.col_0 || item.x,
            value,
          })
        }
      })
      return list.length ? list : []
    },
    borderStyle () {
      return this.config.chartSet.borderStyle.borderTemplate
    },
    style () {
      return this.config.chartSet
    },
    chartSet () {
      return this.config.chartSet
    },
    /** 传给 map series 的数据（分段时含无数据地区默认 0） */
    mapSeriesChartData () {
      if (!this.mapHeatUsesPiecewiseVisualMap()) return this.renderData
      return this.buildPiecewiseMapSeriesData()
    },
  },
  watch: {
    // 配置项变更
    chartSet: {
      handler: function (val) {
        this.init()
      },
      deep: true,
    },
    'config.chartViewData.chartData': {
      async handler () {
        this.init()
      },
    },
    'config.chartViewData.mapRouteOverlay': {
      handler () {
        this.init()
      },
      deep: true,
    },
    'config.chartSet.chartStyle.map': {
      async handler () {
        await this.getMap()
        this.init()
      },
    },
    'config.chartWidth': {
      async handler () {
        this.init()
      },
    },
    'config.chartHeight': {
      async handler () {
        this.init()
      },
    },

    // 全局样式监听
    resolvedChartColorsLabel () {
      this.init()
    },
    async borderStyle (val) {
      if (val) {
        this.borderImgUrl = await imgURLtoBase64(val)
      } else {
        this.borderImgUrl = ''
      }
    },
  },

  async created () {
    if (this.borderStyle) {
      this.borderImgUrl = await imgURLtoBase64(this.borderStyle)
    }
    //  新增地图缩放、轮播延时
    if (!this.chartStyle?.zoom) { this.$set(this.config.chartSet.chartStyle, 'zoom', 1) }
    if (!this.style.chartStyle?.carouselDelay) { this.$set(this.config.chartSet.chartStyle, 'carouselDelay', 3) }
    if (this.chartStyle.heatByValue === undefined) {
      this.$set(this.config.chartSet.chartStyle, 'heatByValue', false)
    }
    const cs = this.config.chartSet.chartStyle
    if (cs.carouselHighlightColor === undefined || cs.carouselHighlightColor === null || cs.carouselHighlightColor === '') {
      this.$set(cs, 'carouselHighlightColor', '#ff3333')
    }
    const routeNum = (k, def) => {
      if (cs[k] === undefined || cs[k] === null) this.$set(cs, k, def)
    }
    routeNum('mapRouteLineWidth', 1.2)
    routeNum('mapRouteTrailLength', 0.35)
    routeNum('mapRouteEffectSymbolSizeW', 4)
    routeNum('mapRouteEffectSymbolSizeH', 5)
    routeNum('mapRouteConstantSpeed', 48)
    routeNum('mapRouteScatterSymbolSize', 9)
    if (cs.mapRouteLineColor === undefined || cs.mapRouteLineColor === null || cs.mapRouteLineColor === '') {
      this.$set(cs, 'mapRouteLineColor', MAP_ROUTE_LINE_COLOR)
    }
    if (cs.visiualMapOption && !cs.visualMapOption) {
      this.$set(cs, 'visualMapOption', cs.visiualMapOption)
    }
    if (cs.hasVisiualMap !== undefined && cs.hasVisualMap === undefined) {
      this.$set(cs, 'hasVisualMap', !!cs.hasVisiualMap)
    }
    if (cs.hasVisualMap === undefined) {
      this.$set(cs, 'hasVisualMap', false)
    }
    if (!cs.visualMapOption) {
      this.$set(cs, 'visualMapOption', { type: 'piecewise', pieces: [], text: [] })
    } else {
      if (!Array.isArray(cs.visualMapOption.pieces)) this.$set(cs.visualMapOption, 'pieces', [])
      if (!Array.isArray(cs.visualMapOption.text)) this.$set(cs.visualMapOption, 'text', [])
      if (cs.visualMapOption.type == null) this.$set(cs.visualMapOption, 'type', 'piecewise')
    }
    if (cs.color === undefined) {
      this.$set(cs, 'color', '#ccc')
    }
    if (cs.heatByValue && cs.hasVisualMap && !cs.visualMapOption.pieces?.length) {
      this.$set(cs.visualMapOption, 'pieces', [{ min: '', max: '', label: '', color: '#5470c6' }])
    }
  },
  async mounted () {
    if (!this.charts) {
      this.charts = echarts.init(this.$refs.mapChart)
    }
    await this.getMap()
    this.init(this.showData)
  },
  updated (val) {
  },
  methods: {
    async getMap () {
      const reqId = ++this.getMapRequestSerial
      const mapKey = (this.config.chartSet.chartStyle.map || []).join('/')
      console.log(mapKey)
      const mapUrl = `https://kylin-static.oss-cn-hangzhou.aliyuncs.com/common/geojson/${mapKey}.json`
      const mapRes = await fetch(mapUrl)
      if (reqId !== this.getMapRequestSerial) return
      const mapJson = await mapRes.json()
      if (reqId !== this.getMapRequestSerial) return
      // 注册地图类型
      this.mapName = randomString()
      echarts.registerMap(this.mapName, mapJson)
      this.mapFeatureNameList = (mapJson.features || [])
        .map(bar => bar.properties && bar.properties.name)
        .filter(Boolean)
      if (this.config.chartSet.chartStyle?.map.length === 1 && !this.mapNameList?.length) { // 长度为1 表示是全国地图
        this.mapNameList = this.mapFeatureNameList.slice()
      }
    },

    /** 地图主 series + 航线/散点的 tooltip */
    formatMapSeriesTooltip (params) {
      if (params.seriesType === 'lines') {
        return '航线（起点→落点）'
      }
      if (params.seriesType === 'scatter' && params.data?.value) {
        const v = params.data.value
        if (Array.isArray(v) && v.length >= 2) {
          return `${params.name}<br/>经度: ${v[0]}<br/>纬度: ${v[1]}`
        }
      }
      const d = params.data
      if (!d) return params.name || ''
      // 指标库：{ x, y→y_0, label } → renderData.value = [ y, label ]，轮播/悬停展示 y 与 label
      if (this.isIndicatorApiChartData && Array.isArray(d.value)) {
        const yDisp = d.value[0]
        const labelDisp = d.value[1]
        const parts = []
        if (yDisp !== undefined && yDisp !== null && yDisp !== '') parts.push(String(yDisp))
        if (labelDisp !== undefined && labelDisp !== null && labelDisp !== '') parts.push(String(labelDisp))
        return parts.join('<br/>') || String(d.name || params.name || '')
      }
      if (Array.isArray(d.value) && this.columnName?.length) {
        let str = `${d.name != null ? d.name : ''}<br/>`
        this.columnName.forEach((item, index) => {
          const val = d.value[index]
          str += `${item}:  ${val !== undefined && val !== null && val !== '' ? val : '-'}<br>`
        })
        return str
      }
      return params.name != null ? String(params.name) : ''
    },

    /** 从地图数据项解析用于热力着色的数值（指标库取 y_0，表格取第一列指标） */
    getHeatNumeric (dataItem) {
      if (!dataItem) return this.mapHeatUsesPiecewiseVisualMap() ? 0 : NaN
      const raw = dataItem.value
      let cell
      if (this.isIndicatorApiChartData && Array.isArray(raw)) {
        cell = raw[0]
      } else if (Array.isArray(raw) && raw.length) {
        cell = raw[0]
      } else if (typeof raw === 'number') {
        return raw
      } else {
        return this.mapHeatUsesPiecewiseVisualMap() ? 0 : NaN
      }
      if (cell === undefined || cell === null || cell === '') {
        return this.mapHeatUsesPiecewiseVisualMap() ? 0 : NaN
      }
      const n = parseFloat(String(cell).replace(/,/g, ''))
      if (!Number.isFinite(n)) {
        return this.mapHeatUsesPiecewiseVisualMap() ? 0 : NaN
      }
      return n
    },

    /** 分段 visualMap：补全 geo 内全部区域，无数据按 0 参与图例命中 */
    buildPiecewiseMapSeriesData () {
      const rd = this.renderData || []
      const normOne = (item) => {
        if (!item || !item.name) return item
        const value = Array.isArray(item.value) ? [...item.value] : []
        const raw0 = value[0]
        const empty = raw0 === undefined || raw0 === null || raw0 === ''
        const n = empty ? NaN : parseFloat(String(raw0).replace(/,/g, ''))
        value[0] = Number.isFinite(n) ? n : 0
        return { name: item.name, value }
      }
      let names = []
      if (this.mapFeatureNameList?.length) {
        names = this.mapFeatureNameList.slice()
      } else if (this.mapNameList?.length) {
        names = this.mapNameList.slice()
      }
      rd.forEach((item) => {
        if (item && item.name && !names.includes(item.name)) names.push(item.name)
      })
      if (!names.length) {
        return rd.map(normOne)
      }
      const byName = {}
      rd.forEach((item) => {
        if (item && item.name) byName[item.name] = item
      })
      return names.map((name) => {
        const ex = byName[name]
        if (ex) return normOne(ex)
        if (this.isIndicatorApiChartData) {
          return { name, value: [0, null] }
        }
        const w = Math.max(1, this.columnName?.length || 0)
        const value = Array(w).fill('')
        value[0] = 0
        return { name, value }
      })
    },

    /** t∈[0,1]：0 偏浅（近白），1 为主题色 */
    heatMixColor (hex, t) {
      if (!hex || hex.length < 7) return hex
      const th = Math.max(0, Math.min(1, t))
      const r = Math.round(255 + (parseInt(hex.slice(1, 3), 16) - 255) * th)
      const g = Math.round(255 + (parseInt(hex.slice(3, 5), 16) - 255) * th)
      const b = Math.round(255 + (parseInt(hex.slice(5, 7), 16) - 255) * th)
      const h = (n) => ('0' + n.toString(16)).slice(-2)
      return `#${h(r)}${h(g)}${h(b)}`
    },

    /** 数值热力 + 分段 visualMap：与 ECharts piecewise 着色二选一，此处不写 regions 颜色避免覆盖 visualMap */
    mapHeatUsesPiecewiseVisualMap () {
      const cs = this.chartStyle
      if (!cs?.heatByValue) return false
      if (!cs.hasVisualMap && !cs.hasVisiualMap) return false
      return this.getSanitizedVisualMapPieces().length > 0
    },

    getSanitizedVisualMapPieces () {
      const vm = this.chartStyle.visualMapOption || this.chartStyle.visiualMapOption
      const raw = vm?.pieces
      if (!Array.isArray(raw) || !raw.length) return []
      return raw.map((p) => {
        if (!p || !p.color) return null
        const o = { color: p.color }
        if (p.label != null && p.label !== '') o.label = String(p.label)
        let hasBound = false
        if (p.min !== '' && p.min !== undefined && p.min !== null && Number.isFinite(Number(p.min))) {
          o.min = Number(p.min)
          hasBound = true
        }
        if (p.max !== '' && p.max !== undefined && p.max !== null && Number.isFinite(Number(p.max))) {
          o.max = Number(p.max)
          hasBound = true
        }
        if (!hasBound) return null
        return o
      }).filter(Boolean)
    },

    buildPiecewiseVisualMapOption () {
      if (!this.mapHeatUsesPiecewiseVisualMap()) return null
      const vmopt = this.chartStyle.visualMapOption || this.chartStyle.visiualMapOption || {}
      const pieces = this.getSanitizedVisualMapPieces()
      const base = {
        type: vmopt.type || 'piecewise',
        show: vmopt.show !== false,
        seriesIndex: 0,
        dimension: 0,
        pieces,
      }
      const tc = this.mapTextColor
      // 分段型 visualMap 用 text（两端文案），title 不在类型里易被忽略；仅一项时补空串以占「另一端」
      const rawText = Array.isArray(vmopt.text) ? vmopt.text.filter(t => t != null && String(t).trim() !== '') : []
      if (rawText.length) {
        base.text = rawText.length === 1 ? [rawText[0], ''] : rawText.slice()
      }
      // 使用 text 时 ECharts 默认会隐藏分段标签，需显式打开
      base.showLabel = vmopt.showLabel !== false
      const passthrough = ['left', 'right', 'top', 'bottom', 'orient', 'padding', 'itemWidth', 'itemHeight', 'itemGap', 'itemSymbol', 'inverse', 'align', 'backgroundColor', 'borderColor']
      passthrough.forEach((k) => {
        if (vmopt[k] !== undefined && vmopt[k] !== '') base[k] = vmopt[k]
      })
      if (base.left == null && base.right == null) {
        base.left = 12
      }
      if (base.top == null && base.bottom == null) {
        base.bottom = 20
      }
      if (!base.orient) {
        base.orient = 'vertical'
      }
      base.textStyle = _.merge({ fontSize: 12 }, vmopt.textStyle || {}, { color: tc })
      // 未命中任一分段、或数值不在区间内：默认全透明底，命中分段后才用 pieces 里的实色
      if (vmopt.outOfRange && typeof vmopt.outOfRange === 'object') {
        base.outOfRange = _.merge({ color: 'rgba(0,0,0,0)' }, vmopt.outOfRange)
      } else {
        base.outOfRange = { color: 'rgba(0,0,0,0)' }
      }
      return base
    },

    buildGeoRegions (baseHex) {
      const heatOn = !!this.chartStyle.heatByValue
      if (!heatOn) {
        return this.renderData.map(bar => ({
          name: bar.name,
          itemStyle: {
            show: true,
            areaColor: baseHex,
            opacity: 0.6,
          },
        }))
      }
      if (this.mapHeatUsesPiecewiseVisualMap()) {
        return []
      }
      const nums = this.renderData.map(bar => this.getHeatNumeric(bar)).filter(n => Number.isFinite(n))
      const min = nums.length ? Math.min(...nums) : 0
      const max = nums.length ? Math.max(...nums) : 0
      return this.renderData.map(bar => {
        const v = this.getHeatNumeric(bar)
        let ratio = 0.5
        if (Number.isFinite(v)) {
          ratio = max === min ? 0.5 : (v - min) / (max - min)
          ratio = Math.max(0, Math.min(1, ratio))
        } else {
          ratio = 0.12
        }
        return {
          name: bar.name,
          itemStyle: {
            show: true,
            areaColor: this.heatMixColor(baseHex, 0.12 + ratio * 0.88),
            opacity: 0.92,
          },
        }
      })
    },

    buildRouteGeoRouteSeries () {
      const overlay = this.config.chartViewData?.mapRouteOverlay
      if (!overlay || (!overlay.scatter?.length && !overlay.lines?.length)) return []
      const cs = this.chartStyle
      const palette = COLOR_LIST.filter(item => { return item.label === this.resolvedChartColorsLabel })[0]?.value || ['#0276FA', '#3ECB6F']
      const accent = palette[1] || palette[0]
      const lineW = parseFloat(cs.mapRouteLineWidth)
      const routeLineWidth = Number.isFinite(lineW) && lineW > 0 ? Math.min(32, lineW) : 1.2
      const trail = parseFloat(cs.mapRouteTrailLength)
      const trailLen = Number.isFinite(trail) ? Math.max(0, Math.min(1, trail)) : 0.35
      const spd = parseFloat(cs.mapRouteConstantSpeed)
      const constantSpeed = Number.isFinite(spd) && spd > 0 ? Math.min(400, Math.max(4, spd)) : 48
      const rawLineColor = cs.mapRouteLineColor
      const routeLineColor = (rawLineColor != null && String(rawLineColor).trim() !== '')
        ? String(rawLineColor).trim()
        : MAP_ROUTE_LINE_COLOR
      const sw = parseFloat(cs.mapRouteEffectSymbolSizeW)
      const sh = parseFloat(cs.mapRouteEffectSymbolSizeH)
      const symW = Number.isFinite(sw) && sw > 0 ? Math.min(48, sw) : 4
      const symH = Number.isFinite(sh) && sh > 0 ? Math.min(48, sh) : 5
      const sc = parseFloat(cs.mapRouteScatterSymbolSize)
      const scatterSymbolSize = Number.isFinite(sc) && sc > 0 ? Math.min(80, sc) : 9

      const extra = []
      if (overlay.scatter?.length) {
        extra.push({
          name: '落点',
          type: 'scatter',
          coordinateSystem: 'geo',
          geoIndex: 0,
          zlevel: 2,
          symbol: 'pin',
          symbolSize: scatterSymbolSize,
          itemStyle: { color: accent },
          data: overlay.scatter.map((d, i) => ({
            name: d.name || `落点${i + 1}`,
            value: d.value,
          })),
        })
      }
      if (overlay.lines?.length) {
        extra.push({
          name: '航线',
          type: 'lines',
          coordinateSystem: 'geo',
          geoIndex: 0,
          zlevel: 3,
          polyline: false,
          effect: {
            show: true,
            constantSpeed,
            trailLength: trailLen,
            symbol: 'arrow',
            symbolSize: [symW, symH],
            color: routeLineColor,
          },
          lineStyle: {
            width: routeLineWidth,
            opacity: 0.92,
            curveness: 0.12,
            color: routeLineColor,
          },
          data: overlay.lines.map(d => ({
            coords: d.coords,
            lineStyle: { ...d.lineStyle, color: routeLineColor },
          })),
        })
      }
      return extra
    },

    async init () {
      if (!this.mapName) return
      const palette = COLOR_LIST.filter(item => { return item.label === this.resolvedChartColorsLabel })[0]
      const baseHex = palette.value[0]
      const piecewiseVm = this.mapHeatUsesPiecewiseVisualMap()
      const carouselOn = !!this.chartStyle.carousel
      const hlRaw = this.chartStyle.carouselHighlightColor
      const carouselHighlight = (hlRaw != null && String(hlRaw).trim() !== '')
        ? String(hlRaw).trim()
        : '#ff3333'
      /** 轮播 tooltip 切换时的区域 emphasis（与手动 hover 共用，仅 carousel 开启时启用） */
      const carouselEmphasisItemStyle = {
        areaColor: carouselHighlight,
        borderColor: carouselHighlight,
        borderWidth: 2,
        opacity: 1,
      }
      const mapSeries = {
        ..._.cloneDeep(mapBaseOption.series[0]),
        data: this.mapSeriesChartData,
        // hover 仅高亮区域，不浮出国家/地区名称（仍可用 tooltip 看数据）
        emphasis: {
          label: { show: false },
          ...(carouselOn ? { itemStyle: { ...carouselEmphasisItemStyle } } : {}),
        },
      }
      if (piecewiseVm) {
        mapSeries.itemStyle = _.merge({}, mapSeries.itemStyle || {}, {
          areaColor: 'rgba(0,0,0,0)',
          borderColor: hexToRgba(baseHex, 0.45),
        })
      }
      const textColor = this.mapTextColor
      this.option = _.merge(_.cloneDeep(mapBaseOption), {
        tooltip: {
          textStyle: {
            fontSize: this.chartStyle.fontSize,
            color: '#ffffff',
          },
          formatter: (params) => this.formatMapSeriesTooltip(params),
        },
        series: [
          mapSeries,
          ...this.buildRouteGeoRouteSeries(),
        ],
        geo: {
          zoom: this.chartStyle.zoom,
          label: {
            show: this.chartStyle.isLabel,
            fontSize: this.chartStyle.fontSize,
            color: textColor,
          },
          map: this.mapName,
          emphasis: {
            label: {
              show: false,
              fontSize: this.chartStyle.fontSize,
              color: textColor,
            },
            itemStyle: piecewiseVm
              ? (carouselOn
                ? { ...carouselEmphasisItemStyle }
                : {
                  borderWidth: 2,
                  borderColor: hexToRgba(baseHex, 0.95),
                })
              : (carouselOn
                ? { ...carouselEmphasisItemStyle }
                : {
                  areaColor: baseHex,
                }),
          },
          itemStyle: {
            borderWidth: 1, // 边际线大小
            borderColor: hexToRgba(baseHex, piecewiseVm ? 0.45 : 0.8), // 分段模式底图透明，边界略淡以免抢眼
            // 分段模式：默认无底色，仅 visualMap 命中分段后上色（不透明实色由 pieces.color 决定）
            areaColor: piecewiseVm ? 'rgba(0,0,0,0)' : baseHex,
            opacity: piecewiseVm ? 1 : 0.3,
          },
          regions: this.buildGeoRegions(baseHex),
        },
      })

      const pieceVm = this.buildPiecewiseVisualMapOption()
      if (pieceVm) {
        this.option.visualMap = pieceVm
      } else {
        delete this.option.visualMap
      }

      this.$nextTick(() => {
        this.charts.setOption(this.option, true)
        this.charts.resize()
      })

      if (this.chartStyle.carousel && this.mapSeriesChartData.length > 0) {
        this.mouseEvents()
        this.tooltipsActive()
      } else {
        if (this.timer) {
          clearInterval(this.timer)
          this.index = -1
        }
      }
    },

    // 提示轮播
    tooltipsActive () {
      if (this.timer) {
        clearInterval(this.timer)
      }
      const slice = this.mapSeriesChartData
      const dataLength = slice.length
      if (dataLength <= 0) {
        this.index = -1
        return
      }
      // 用定时器控制高亮
      this.timer = setInterval(() => {
        // 清除之前的高亮
        this.charts.dispatchAction({
          type: 'downplay',
          seriesIndex: 0,
          dataIndex: this.index,
        })
        if (this.index === dataLength - 1) {
          this.index = 0
        } else {
          this.index++
        }
        // 不在此 setOption 全量配置：会打断 lines 的飞线动效导致卡顿

        // 当前下标高亮
        this.charts.dispatchAction({
          type: 'highlight',
          seriesIndex: 0,
          dataIndex: this.index,
        })
        this.charts.dispatchAction({
          type: 'showTip',
          seriesIndex: 0,
          dataIndex: this.index,
        })
        // 联动 eventBus 事件触发
        relateBus.$emit('activeChange', this.config.addSn, slice[this.index])
      }, this.config.chartSet.chartStyle.carouselDelay * 1000)
    },

  },
}
</script>

<style lang='scss' scoped>
.map-chart{
  height:100%;
  display: flex;
  flex-direction: column;
  &-wrapper {
    position: relative;
    width:100%;
    margin-bottom: 10px;
    overflow: visible;
  }
  &-border {
    position: absolute;
    top: 0;
    height: 100%;
    z-index: -1;

  }
  &-title {
    padding:7px 30px;
  }

  .map-chart-container{
    flex: 1;
  }
}
</style>
